import { PlusOutlined } from "@ant-design/icons";
import { Button, Col, Divider, Flex, Select } from "antd";
import React from "react";
import { NoteType } from "../../domain/notes.domain";
import { PRACTICE_AREA } from "../../domain/user.domain";
import { useAppDispatch, useAppSelector } from "../../hooks/redux-hooks";
import { useIsMobileView } from "../../hooks/ui-hook";
import { useFuseSearch } from "../../hooks/useFuseSearch";
import { getCustomNoteList } from "../../service/notes.service";
import { getTemplates } from "../../service/templates.service";
import {
  setAITemplateModalState,
  setAllTemplates,
  setShowRestrictionModal,
  setTemplateOpenedFromNoteModal,
} from "../../slices/appStateSlice";
import { setRecordingDetail } from "../../slices/recordingsSlice";
import { LC_KEYS } from "../../utils/constants";
import type { CustomNote } from "./AITemplateModal/types";
import styles from "./createnotemodal.module.scss";

const SOAP_NOTE_SLUG = "soap-note";

const NoteTypeSelector = ({ noteTypes }: { noteTypes: { name: string; type: string }[] }) => {
  const componentName = "createnotemodal";
  const dispatch = useAppDispatch();
  const isMobile = useIsMobileView();
  const { recordingDetail } = useAppSelector((state) => state.recordings);
  const [customNoteList, setCustomNoteList] = React.useState<CustomNote[]>([]);
  const { templateOpenedFromNoteModal, templates } = useAppSelector((state) => state.appState);
  const { isMobilePath } = useAppSelector((state) => state.appState);
  const userInfo = useAppSelector((state) => state.auth.userInfo);
  const fetchCustomNoteList = async () => {
    const response = await getCustomNoteList();
    let data = await response.data;
    data = data.filter((item: CustomNote) => item.status === 0);
    setCustomNoteList(data as CustomNote[]);
  };

  const fetchAllTemplates = async () => {
    getTemplates()
      .then((response) => {
        const sortedTemplates = [...response].sort((a, b) => {
          // Put soap-note template first
          if (a.slug === SOAP_NOTE_SLUG) return -1;
          if (b.slug === SOAP_NOTE_SLUG) return 1;
          // Sort the rest alphabetically by name
          return a.name.localeCompare(b.name);
        });
        dispatch(setAllTemplates(sortedTemplates));
      })
      .catch((error) => {
        console.error("Error fetching templates:", error);
      });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: need to fetch custom note list when modal is opened
  React.useEffect(() => {
    fetchCustomNoteList();
    fetchAllTemplates();
  }, [templateOpenedFromNoteModal]);

  const allNoteTypes = [
    ...(customNoteList.length > 0
      ? [
          {
            label: "My Templates",
            title: "My Templates",
            options: customNoteList.map((note: CustomNote) => {
              if (note.is_group_accessible) {
                return {
                  label: (
                    <>
                      {note.name}
                      <span style={{ fontStyle: "italic", fontSize: "0.9em", marginLeft: "4px" }}>
                        - shared by group admin
                      </span>
                    </>
                  ),
                  value: note.id,
                };
              }
              return {
                label: note.name,
                value: note.id,
              };
            }),
          },
        ]
      : []),
    // Only show Nudge Templates for Behavioral Health users
    ...(userInfo?.practice_area === PRACTICE_AREA.BEHAVIORAL_HEALTH
      ? [
          {
            label: "Nudge Templates",
            title: "Nudge Templates",
            options: noteTypes.map((note: any) => ({
              label: note.name,
              value: Number.parseInt(NoteType[note.type]), // Assuming note.type is the string value for the option
            })),
          },
        ]
      : []),
    ...(templates.length > 0
      ? [
          {
            label: "Template Gallery",
            title: "Template Gallery",
            options: templates.map((note: any) => ({
              label: note.name,
              value: note.slug, // Already a string, ensure consistency
              id: note.id,
              is_template: true,
              content: note.content,
              description: note.description,
              tags: note.tags,
            })),
          },
        ]
      : []),
  ];
  const { search: fuseSearch } = useFuseSearch({
    data: allNoteTypes.flatMap((group) => group.options),
    keys: ["label"],
  });

  function handleAddTemplate() {
    if (isMobile) {
      alert("This feature is available on Desktop");
    } else if (isMobilePath) {
      dispatch(setShowRestrictionModal(true));
    } else {
      dispatch(setAITemplateModalState(true));
      dispatch(setTemplateOpenedFromNoteModal(true));
    }
  }

  return (
    <Col span={24} className={`${styles[`${componentName}__modal-row`]}`}>
      <Select
        value={recordingDetail?.note_type}
        listHeight={isMobile ? 160 : 256}
        onChange={(value, option: any) => {
          if (!option.is_template) {
            localStorage.setItem(LC_KEYS.NOTE_TYPE, value.toString());
            localStorage.setItem(LC_KEYS.IS_TEMPLATE, "false");
          } else {
            localStorage.removeItem(LC_KEYS.NOTE_TYPE);
            localStorage.removeItem(LC_KEYS.IS_TEMPLATE);
          }
          dispatch(
            setRecordingDetail({
              ...recordingDetail,
              note_type: value,
              template_id: option?.id,
              is_template: option?.is_template,
              modality_type: value === NoteType.CONSULT_NOTE ? "write" : recordingDetail.modality_type, // Compare string with string representation of enum
            }),
          );
        }}
        showSearch
        placeholder="Select a document type"
        optionFilterProp="children"
        // Use Fuse.js for fuzzy search in dropdown
        filterOption={(input, option: any) => {
          // Use any for option
          if (!input) return true;
          const results = fuseSearch(input);
          return results.some((item: any) => item.value === option.value); // Use any for item
        }}
        dropdownRender={(menu) => (
          <div className="client">
            {menu}
            <Divider style={{ margin: "4px 0" }} />
            <Flex gap={10}>
              <Button type="text" icon={<PlusOutlined />} onClick={handleAddTemplate}>
                Add My Template
              </Button>
            </Flex>
          </div>
        )}
        className={`${styles[`${componentName}__modal-row-field`]}`}
        options={allNoteTypes}
      />
    </Col>
  );
};

export default NoteTypeSelector;
